/**
 * OnboardingWizard root — FE-W-03.
 *
 * 책무:
 *   1. 마운트 시 restoreSession 호출 — 서버의 currentStep 으로 자동 redirect
 *   2. Stepper 표시 (1~4 단계, 진행도)
 *   3. nested <Outlet/> 으로 각 Step 컴포넌트 렌더
 *   4. server currentStep ↔ URL 정합성 강제 — URL 직접 이동 `/onboarding/admin` 시 currentStep<3 이면 자동 redirect
 *
 * AuthGuard 외부 — 로그인 안 한 사용자도 접근 (router 가 분기).
 */

import { useEffect, useRef, useSyncExternalStore } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Stepper, type StepperItem } from '../../components/shared/Stepper';
import { useOnboardingStore, type OnboardingStep } from '../../store/onboardingStore';
import { tOnb, getOnboardingLang, subscribeOnboardingLang } from '../../lib/onboarding-i18n';
import './OnboardingWizard.css';

/** step → URL path 매핑. */
function pathForStep(step: OnboardingStep): string {
  // CRITICAL-1 봉합: server currentStep=N → user 가 Step{N} form 에 있어야 함.
  // step=1 → /onboarding (Step1 form)
  // step=2 → /onboarding/domain (Step2 form)
  // step=3 → /onboarding/verify (Step3 form)
  // step=4 → /onboarding/admin (Step4 form, finalize 직전)
  switch (step) {
    case 0:
    case 1:
      return '/onboarding';
    case 2:
      return '/onboarding/domain';
    case 3:
      return '/onboarding/verify';
    case 4:
      return '/onboarding/admin';
  }
}

/** URL → 의도된 step (사용자가 직접 navigate 한 의도 추정). */
function stepFromPath(pathname: string): OnboardingStep {
  if (pathname.endsWith('/onboarding/admin')) return 4;
  if (pathname.endsWith('/onboarding/verify')) return 3;
  if (pathname.endsWith('/onboarding/domain')) return 2;
  return 1;
}

const STEP_LABEL_KEYS = [
  'onboarding.step.label.company',
  'onboarding.step.label.domain',
  'onboarding.step.label.verify',
  'onboarding.step.label.admin',
] as const;

export function OnboardingWizard() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentStep = useOnboardingStore((s) => s.currentStep);
  const loading = useOnboardingStore((s) => s.loading);
  const restore = useOnboardingStore((s) => s.restore);

  // lang 변경 시 re-render (cycle 41 패턴)
  useSyncExternalStore(subscribeOnboardingLang, getOnboardingLang, getOnboardingLang);

  const restoredRef = useRef(false);

  // 마운트 시 1회만 restore — race / 무한 루프 차단.
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;
    void restore().then((result) => {
      if (result.status === 'expired') {
        navigate('/onboarding/expired', { replace: true });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Phase 4 CRITICAL-1 봉합 — server 의 currentStep 의미:
  //   "현재 input 받는 단계 (= 사용자가 작성 중인 form 번호)".
  // 즉 currentStep=N 이면 Step{N} form 진행 가능 + 다음 step 의 form 도 navigate 가능
  // (사용자가 submit 후 frontend 가 navigate 하지만 서버 advance 는 그 form submit 호출 시점).
  //
  // 따라서 navigable 한 최대 step = currentStep (clamp 0→1, 최대 4).
  // intended > navigable 일 때만 redirect — Step1 통과 후 /onboarding/domain navigate 가
  // intendedStep(2) === navigable(2) 로 허용됨.
  useEffect(() => {
    if (
      location.pathname.includes('/expired') ||
      location.pathname.includes('/race-rejected')
    ) {
      return;
    }
    const intendedStep = stepFromPath(location.pathname);
    // currentStep === 0 (uninitialized) → Step1 form 만 허용.
    // 그 외에는 currentStep 자체가 navigable (frontend 가 submit 응답으로 advance 받음).
    const navigableMax = (currentStep === 0 ? 1 : currentStep) as OnboardingStep;
    if (intendedStep > navigableMax) {
      const target = pathForStep(navigableMax);
      if (location.pathname !== target) {
        navigate(target, { replace: true });
      }
    }
  }, [currentStep, location.pathname, navigate]);

  // Stepper items — currentStep 기준 active/done/pending 분류.
  const effectiveStep = currentStep === 0 ? 1 : currentStep;
  const steps: StepperItem[] = STEP_LABEL_KEYS.map((labelKey, idx) => {
    const stepNum = idx + 1;
    let status: StepperItem['status'];
    if (stepNum < effectiveStep) status = 'done';
    else if (stepNum === effectiveStep) status = 'active';
    else status = 'pending';
    return { id: `onboarding-step-${stepNum}`, label: tOnb(labelKey), status };
  });

  // expired/race-rejected 는 Stepper 숨김 (이미 사이클 종료).
  const isSpecialPage =
    location.pathname.includes('/expired') ||
    location.pathname.includes('/race-rejected');

  return (
    <div className="onboarding-wizard">
      <header className="onboarding-wizard__header">
        <div className="onboarding-wizard__brand">
          <div className="onboarding-wizard__brand-mark">A</div>
          <span className="onboarding-wizard__brand-name">Axi</span>
        </div>
        <h1 className="onboarding-wizard__title">{tOnb('onboarding.wizard.title')}</h1>
        <p className="onboarding-wizard__subtitle">
          {tOnb('onboarding.wizard.subtitle')}
        </p>
      </header>

      {!isSpecialPage && (
        <nav className="onboarding-wizard__stepper" aria-label={tOnb('onboarding.wizard.nav_aria')}>
          <Stepper steps={steps} orientation="horizontal" aria-label={tOnb('onboarding.wizard.stepper_aria')} />
        </nav>
      )}

      <main className="onboarding-wizard__content" aria-live="polite" aria-busy={loading}>
        {loading && currentStep === 0 ? (
          <div className="onboarding-wizard__loading" role="status">
            {tOnb('onboarding.wizard.restoring')}
          </div>
        ) : (
          <Outlet />
        )}
      </main>
    </div>
  );
}
